import { ChannelList, FilamentList, PrinterList, SetupStatus } from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ErrorBox } from '../../components/States';
import { api } from '../../lib/api';
import { useIsAdmin } from '../../lib/session';
import { useSettings, useSettingsDefaults } from '../../lib/settings-api';
import { setupKey, useSetupStatus } from '../../lib/setup-api';
import { SetupChecklist } from './SetupChecklist';
import { StepBusiness } from './StepBusiness';
import { StepChannels } from './StepChannels';
import { StepCosts } from './StepCosts';
import { StepDone } from './StepDone';
import { StepFilaments } from './StepFilaments';
import { StepPrinter } from './StepPrinter';
import { STEPS, type StepState, Stepper } from './WizardParts';

/** Configuração inicial (design Wizard-Custos, 6 steps). Admin only. */
export function SetupWizard() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const step = Math.min(6, Math.max(1, Number(params.get('passo')) || 1));
  const go = (n: number) => {
    setParams({ passo: String(n) });
    window.scrollTo({ top: 0 });
  };

  const status = useSetupStatus();
  const settings = useSettings();
  const defaults = useSettingsDefaults();
  const printers = useQuery({
    queryKey: ['printers'],
    queryFn: () => api.get('/printers?limit=200', PrinterList),
  });
  const filaments = useQuery({
    queryKey: ['filaments'],
    queryFn: () => api.get('/filaments?limit=200', FilamentList),
  });
  const channels = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get('/channels?limit=200', ChannelList),
  });
  const complete = useMutation({
    mutationFn: () => api.post('/setup/complete', SetupStatus),
    onSuccess: (s) => qc.setQueryData(setupKey, s),
  });
  const reachedDone = step === 6;
  const completed = Boolean(status.data?.completedAt);
  const mutateComplete = complete.mutate;
  useEffect(() => {
    if (reachedDone && isAdmin && !completed) mutateComplete();
  }, [reachedDone, isAdmin, completed, mutateComplete]);

  if (!isAdmin) {
    return (
      <div className="col" style={{ gap: 18, maxWidth: 560 }}>
        <h1>Configuração inicial</h1>
        <p className="muted">
          Só o Admin configura custos, impressora, filamentos e canais. Veja o que falta:
        </p>
        <SetupChecklist />
      </div>
    );
  }

  const queries = [status, settings, defaults, printers, filaments, channels];
  if (queries.some((q) => q.isError)) {
    return (
      <ErrorBox
        title="Não foi possível carregar a configuração"
        onRetry={() => queries.forEach((q) => void q.refetch())}
      />
    );
  }
  if (
    !status.data ||
    !settings.data ||
    !defaults.data ||
    !printers.data ||
    !filaments.data ||
    !channels.data
  ) {
    return <div className="skel" style={{ height: 400 }} aria-busy="true" />;
  }

  const s = status.data;
  const done = (key: 'costs' | 'printer' | 'filament' | 'channel') =>
    s.items.find((i) => i.key === key)?.done ?? false;
  const states: StepState[] = [
    s.business === 'done' ? 'done' : s.business === 'skipped' ? 'skip' : 'todo',
    done('costs') ? 'done' : 'todo',
    done('printer') ? 'done' : 'todo',
    done('filament') ? 'done' : 'todo',
    done('channel') ? 'done' : 'todo',
    s.readyToQuote ? 'todo' : 'locked',
  ];
  const doneCount = states.filter((x) => x === 'done' || x === 'skip').length;
  const next = () => go(step + 1);
  const back = () => go(step - 1);

  return (
    <div className="col" style={{ gap: 18 }}>
      <div className="row between wrap" style={{ gap: 12 }}>
        <div>
          <div className="crumb">
            Configuração inicial · {step} de {STEPS.length}
          </div>
          <div className="layers thin" style={{ width: 220, marginTop: 10 }} aria-hidden="true">
            <div className="fill" style={{ width: `${(doneCount / STEPS.length) * 100}%` }} />
          </div>
        </div>
        <div className="col" style={{ gap: 2, alignItems: 'flex-end' }}>
          <Link to="/">Terminar depois</Link>
          <span className="xs muted">Um aviso no início lembra o que falta.</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <Stepper current={step} states={states} onPick={go} />
        </div>
        <div className="card col" style={{ flex: '4 1 560px', minWidth: 0, gap: 18 }}>
          {step === 1 ? <StepBusiness settings={settings.data} onNext={next} /> : null}
          {step === 2 ? (
            <StepCosts
              settings={settings.data}
              defaults={defaults.data}
              onBack={back}
              onNext={next}
            />
          ) : null}
          {step === 3 ? (
            <StepPrinter
              printers={printers.data.data}
              tariff={
                settings.data.costs.energyTariffPerKwh ??
                defaults.data.costs.energyTariffPerKwh ??
                0
              }
              onBack={back}
              onNext={next}
            />
          ) : null}
          {step === 4 ? (
            <StepFilaments filaments={filaments.data.data} onBack={back} onNext={next} />
          ) : null}
          {step === 5 ? (
            <StepChannels
              channels={channels.data.data}
              settings={settings.data}
              onBack={back}
              onNext={next}
            />
          ) : null}
          {step === 6 ? <StepDone status={s} onBack={back} onGo={go} /> : null}
        </div>
      </div>
    </div>
  );
}
